/**
 * Componente container para input de chave NF-e/CT-e
 * Usa hooks customizados e componentes menores para melhor organização.
 *
 * @example
 * <ChaveInput className="w-full" />
 */

"use client";
import { useChaveNfeCard } from "@PN/inclusao";
import { useCallback } from "react";
import { Input } from "visuais";
import { useAccess } from "store";
import { useUser } from "store/_auth.hooks";
import { identificacaoUtils } from "../_utils";
import { useChaveInput } from "./hooks/useChaveInput";
import { useChaveActions } from "./hooks/useChaveActions";
import { ChaveInputHeader } from "./components/ChaveInputHeader";
import { ChaveInputActions } from "./components/ChaveInputActions";

interface ChaveInputProps {
  className?: string;
  showLabel?: boolean;
}

export function ChaveInput({ className }: ChaveInputProps) {
  const { isLoading, error, xmlHistory, xmlData } = useChaveNfeCard();
  const { item: pageAccess } = useAccess();
  const user = useUser();
  // ✅ Permite acesso admin para pedro.leite
  const isAdmin = pageAccess?.isAdmin || user?.login === "pedro.leite" || false;

  // Hooks customizados
  const {
    isPopoverOpen,
    setIsPopoverOpen,
    validarInclusao,
    setValidarInclusao,
    isDownloadingDanfe,
    danfeError,
    handleSearch,
    handleHistorySelect,
    handleClearHistory,
    handleDownloadDanfe,
  } = useChaveActions();

  // Hook do input com busca automática
  const { inputValue, setInputValue, handleInputChange, handleInputBlur } =
    useChaveInput((chave: string) => handleSearch(chave));

  // Detecta o tipo de documento
  const tipoDocumento =
    identificacaoUtils.xml.detectTypeByData(xmlData) ||
    identificacaoUtils.xml.detectTypeByKey(inputValue);

  // Handlers específicos
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleSearch(inputValue);
      }
    },
    [handleSearch, inputValue]
  );

  const handleDownload = useCallback(() => {
    handleDownloadDanfe(inputValue);
  }, [handleDownloadDanfe, inputValue]);

  const handleHistoryItemSelect = useCallback(
    (chave: string) => {
      setInputValue(chave);
      handleHistorySelect(chave);
    },
    [setInputValue, handleHistorySelect]
  );

  return (
    <div className={`space-y-2 ${className}`}>
      <ChaveInputHeader
        inputValue={inputValue}
        xmlData={xmlData}
        isAdmin={isAdmin}
        validarInclusao={validarInclusao}
        onValidarInclusaoChange={setValidarInclusao}
      />

      <div className="relative w-full">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={
            tipoDocumento
              ? `Chave ${tipoDocumento.tipo} (aceita formatação)`
              : "Chave SPED ou CT‑e (aceita formatação)"
          }
          className={`pr-15 text-cyan-800 dark:text-cyan-300 border-cyan-500 text-[7.5pt] ${
            error ? "border-destructive focus-visible:ring-destructive" : ""
          }`}
          aria-invalid={!!error}
          aria-describedby={error ? "xml-search-error" : undefined}
          maxLength={70}
        />

        <ChaveInputActions
          inputValue={inputValue}
          isLoading={isLoading}
          error={error}
          xmlHistory={xmlHistory}
          isPopoverOpen={isPopoverOpen}
          isDownloadingDanfe={isDownloadingDanfe}
          onDownloadDanfe={handleDownload}
          onHistorySelect={handleHistoryItemSelect}
          onClearHistory={handleClearHistory}
          onPopoverOpenChange={setIsPopoverOpen}
        />
      </div>

      {error && !isLoading && (
        <p id="xml-search-error" className="text-xs text-destructive mt-1 ml-1">
          {error}
        </p>
      )}

      {danfeError && (
        <p
          id="danfe-download-error"
          className="text-xs text-destructive mt-1 ml-1"
        >
          Erro no download da DANFE: {danfeError}
        </p>
      )}
    </div>
  );
}
